import { useState, type ReactElement } from 'react';
import { Plus, Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { toast } from '@/hooks/useToast';

import {
  isTenantAdminAccessDenied,
  useCreateTenantRole,
  useDeleteTenantRole,
  useTenantRoles,
  type TenantRoleViewModel,
} from '../api';
import { DeleteTenantRoleConfirmDialog } from './DeleteTenantRoleConfirmDialog';
import { TenantRoleFormDialog, type TenantRoleFormInput } from './TenantRoleFormDialog';
import { TenantRolesTable } from './TenantRolesTable';

const EMPTY_CELL = '—';

/**
 * "Roles" tab body for the tenant detail view (PTV2-1426 / 1444). Search + create + delete over a
 * tenant's own role catalogue; clicking a role opens its record page where description + permissions
 * are edited. This is the SAME catalogue the auth-provider role pickers read from.
 */
export function TenantRolesPanel({ tenantUuid }: { readonly tenantUuid: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const debouncedQuery = useDebouncedValue(searchInput, 300);

  const rolesQuery = useTenantRoles(tenantUuid, debouncedQuery);
  const createRole = useCreateTenantRole();
  const deleteRole = useDeleteTenantRole();

  const [formOpen, setFormOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<TenantRoleViewModel | null>(null);

  const roles = rolesQuery.data ?? [];

  function openCreate(): void {
    createRole.reset();
    setFormOpen(true);
  }

  function openRole(role: TenantRoleViewModel): void {
    void navigate(`/platform/tenants/${tenantUuid}/roles/${role.id}`);
  }

  function handleFormSubmit(input: TenantRoleFormInput): void {
    createRole.mutate(
      { tenantUuid, name: input.name, description: input.description },
      {
        onSuccess: () => {
          setFormOpen(false);
          createRole.reset();
          toast({ title: t('routes.platform.detail.roles.form.created'), variant: 'success' });
        },
      },
    );
  }

  function handleDeleteConfirm(role: TenantRoleViewModel): void {
    deleteRole.mutate(
      { tenantUuid, guid: role.id },
      {
        onSuccess: () => {
          setPendingDelete(null);
          deleteRole.reset();
        },
      },
    );
  }

  if (rolesQuery.isError && isTenantAdminAccessDenied(rolesQuery.error)) {
    return (
      <p role="alert" className="max-w-xl text-sm text-muted-foreground">
        {t('routes.platform.detail.roles.accessDenied')}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="relative max-w-md flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); }}
            placeholder={t('routes.platform.detail.roles.searchPlaceholder')}
            aria-label={t('routes.platform.detail.roles.searchAriaLabel')}
            className="pl-9 pr-9"
            autoComplete="off"
            spellCheck={false}
          />
          {searchInput.length > 0 && (
            <button
              type="button"
              onClick={() => { setSearchInput(''); }}
              aria-label={t('routes.platform.detail.roles.clearSearch')}
              className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>
        <Button size="sm" onClick={openCreate}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('routes.platform.detail.roles.createAction')}
        </Button>
      </div>

      {rolesQuery.isPending && (
        <div className="space-y-2" aria-hidden="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full rounded-md" />
          ))}
        </div>
      )}

      {rolesQuery.isError && (
        <div className="max-w-xl">
          <FetchErrorCallout
            error={rolesQuery.error}
            heading={t('routes.platform.detail.roles.error.heading')}
            message={t('routes.platform.detail.roles.error.message')}
            codeLabel={t('routes.platform.detail.roles.error.codeLabel')}
            onRetry={() => { rolesQuery.refetch().catch(() => undefined); }}
            retryLabel={t('routes.platform.detail.roles.error.retry')}
          />
        </div>
      )}

      {rolesQuery.isSuccess && roles.length === 0 && (
        <p className="text-sm text-muted-foreground" role="status">
          {debouncedQuery.trim().length > 0
            ? t('routes.platform.detail.roles.searchEmpty', { query: debouncedQuery })
            : t('routes.platform.detail.roles.empty')}
        </p>
      )}

      {rolesQuery.isSuccess && roles.length > 0 && (
        <DataTable aria-label={t('routes.platform.detail.roles.table.ariaLabel')} className="flex flex-col">
          <TenantRolesTable roles={roles} emptyCell={EMPTY_CELL} onOpen={openRole} onDelete={setPendingDelete} />
        </DataTable>
      )}

      <TenantRoleFormDialog
        open={formOpen}
        initial={null}
        onOpenChange={(next) => {
          if (!next) {
            createRole.reset();
          }
          setFormOpen(next);
        }}
        onSubmit={handleFormSubmit}
        pending={createRole.isPending}
        submitError={createRole.error}
      />

      <DeleteTenantRoleConfirmDialog
        role={pendingDelete}
        onCancel={() => {
          if (deleteRole.isPending) return;
          setPendingDelete(null);
          deleteRole.reset();
        }}
        onConfirm={handleDeleteConfirm}
        pending={deleteRole.isPending}
        submitError={deleteRole.error}
      />
    </div>
  );
}
